// Murphy returns one Problem Alignment match per prospect QUOTE, each carrying
// the full text of the company problem it maps to. A company with one headline
// problem therefore got the same heading on every card. This groups the
// matches so a problem is shown once with its quotes underneath; used by the
// in-app report and the PDF so both read the same. Display-only: stored
// analyses are untouched, so old reports are tidied too.

export const MAX_QUOTES_PER_PROBLEM = 3;
const FALLBACK_PROBLEM = 'Addressable problem';
const EVIDENCE_RANK = { explicit: 0, implied: 1, possible: 2 };

const normalise = (text) => String(text || '').toLowerCase().replace(/\s+/g, ' ').trim();
const rank = (evidence) => (evidence in EVIDENCE_RANK ? EVIDENCE_RANK[evidence] : 3);

export const groupAlignmentMatches = (matches, { maxQuotes = MAX_QUOTES_PER_PROBLEM } = {}) => {
  const groups = [];
  const byKey = new Map();

  (Array.isArray(matches) ? matches : []).forEach((m, order) => {
    if (!m || typeof m !== 'object') return;
    const problem = String(m.addressableProblem || m.title || '').trim() || FALLBACK_PROBLEM;
    const key = normalise(problem);
    let group = byKey.get(key);
    if (!group) {
      group = { problem, quotes: [], offerings: [] };
      byKey.set(key, group);
      groups.push(group);
    }

    // Offerings come from every match in the group, including ones whose quote
    // is trimmed below, so a tag is never lost to the quote limit.
    (Array.isArray(m.matchingOfferings) ? m.matchingOfferings : []).forEach((name) => {
      if (typeof name !== 'string' || !name.trim()) return;
      if (!group.offerings.some((o) => normalise(o) === normalise(name))) group.offerings.push(name.trim());
    });

    const text = String(m.prospectStatement || m.quote || '').trim();
    if (!text || group.quotes.some((q) => normalise(q.text) === normalise(text))) return;
    group.quotes.push({
      text,
      speaker: m.speaker && m.speaker !== 'unknown' ? String(m.speaker) : '',
      evidence: String(m.evidenceStrength || '').toLowerCase(),
      order,
    });
  });

  return groups.map((g) => ({
    problem: g.problem,
    // Strongest evidence first; transcript order breaks ties.
    quotes: [...g.quotes]
      .sort((a, b) => (rank(a.evidence) - rank(b.evidence)) || (a.order - b.order))
      .slice(0, Math.max(0, maxQuotes))
      .map(({ text, speaker, evidence }) => ({ text, speaker, evidence })),
    offerings: g.offerings,
  }));
};

export default groupAlignmentMatches;
